import React, { useContext, useState } from 'react'
import 'react-phone-input-2/lib/style.css'
import PhoneInput from 'react-phone-input-2'
import { styled } from '@mui/material/styles'
import { isValidPhoneNumber, CountryCode } from 'libphonenumber-js'
import AppContext from '../../context/appContext'
import { useMediaQuery } from 'react-responsive'

const StyledPhoneInput = styled(PhoneInput, {
	shouldForwardProp: (prop) => prop !== 'isDarkMode',
})<{ isDarkMode: boolean }>(({ isDarkMode }) => ({
	'& .special-label': {
		display: 'none',
	},
	'& .form-control': {
		width: '100%',
		border: '1px solid #C7C7C7',
		borderRadius: '4px',
		fontSize: '16px',
		fontWeight: '700',
		height: '45px',
	},
	'& .react-tel-input': {
		border: 'none',
	},
	'& .flag-dropdown': {
		borderRadius: '8px 0 0 8px',
		backgroundColor: isDarkMode ? '#333333 !important' : '#F8F8F8 !important',
		border: isDarkMode ? '1px solid rgb(115 115 115)' : '1px solid #C7C7C7',
		'& .selected-flag': {
			borderRadius: '8px 0 0 8px',
			backgroundColor: isDarkMode ? '#333333' : '#F8F8F8',
		},
		'&.open .selected-flag': {
			borderRadius: '8px 0 0 8px',
			backgroundColor: isDarkMode ? '#333333' : '#F8F8F8',
		},
	},
}))

interface PhoneNumberFieldProps {
	value: string
	onChange: (value: string) => void
	setError: (error: string | false) => void
	showError?: boolean
	height?: string
	width?: string
	isApp?: boolean
}

interface CountryData {
	iso2: string
	[key: string]: any
}

const PhoneNumberField: React.FC<PhoneNumberFieldProps> = ({
	value,
	onChange,
	setError,
	showError,
	height = '45px',
	width = '100%',
	isApp,
}) => {
	const [isValid, setIsValid] = useState<boolean>(true)
	const isMobile = useMediaQuery({ maxWidth: 1024 })

	const { darkMode } = useContext(AppContext)
	const isDarkTheme =
		darkMode ||
		(typeof document !== 'undefined' &&
			document.documentElement.classList.contains('dark'))

	const handlePhoneChange = (phoneValue: string, countryData: CountryData) => {
		onChange(phoneValue)

		const fullPhoneNumber = `+${phoneValue.replace(/[^\d]/g, '')}`

		const valid = phoneValue.length
			? isValidPhoneNumber(fullPhoneNumber, countryData.iso2 as CountryCode)
			: true
		setIsValid(valid)

		setError(!valid ? 'Invalid phone number for selected country' : false)
	}
	const borderStyle =
		!isValid || showError !== false
			? '1px solid rgb(210 40 40)'
			: isDarkTheme
				? '1px solid rgb(115 115 115)'
				: '1px solid #C7C7C7'

	const fontSize = isMobile || isApp ? '16px' : '14px'

	return (
		<div className="roboto-font" style={{ width }}>
			<StyledPhoneInput
				isDarkMode={isDarkTheme}
				country={'us'}
				value={value}
				onChange={handlePhoneChange}
				inputStyle={{
					width: width,
					border: borderStyle,
					height,
					fontSize: fontSize,
					fontWeight: '400',
					borderRadius: '8px',
					backgroundColor: isDarkTheme ? 'rgb(51 51 51)' : '#fff',
					color: isDarkTheme ? '#fff' : '#000',
				}}
				containerStyle={{ width: '100%', border: 'none', height, }}
				buttonStyle={{
					border: borderStyle,
					borderRadius: '8px 0 0 8px',
					backgroundColor: isDarkTheme ? 'rgb(51 51 51)' : '#fff',
					color: isDarkTheme ? '#fff' : '#000',
				}}
				dropdownStyle={{
					border: isDarkTheme ? '1px solid rgb(115 115 115)' : '1px solid #C7C7C7',
					borderRadius: '8px 8px',
					marginTop: '2px',
					backgroundColor: isDarkTheme ? 'rgb(51 51 51)' : '#fff',
					color: isDarkTheme ? '#fff' : '#000',
				}}
			/>
			{!isValid ||
				(showError !== false && (
					<div className="text-[#FF5353] text-[13px]">Invalid phone number</div>
				))}
			<style>
				{`
        .react-tel-input .flag-dropdown.open .selected-flag {
        background: ${isDarkTheme ? '#333333' : '#F8F8F8'} !important;
        border-radius: 8px 0 0 8px !important;
        }

        .react-tel-input .country-list {
        background-color: ${isDarkTheme ? '#333333' : '#ffffff'} !important;
        border: 1px solid ${isDarkTheme ? 'rgb(115 115 115)' : '#C7C7C7'} !important;
        color: ${isDarkTheme ? '#ffffff' : '#000000'} !important;
        border-radius: 8px !important;
        margin-top: 4px !important;
        }

        .react-tel-input .country-list .country {
        background-color: transparent !important;
        color: ${isDarkTheme ? '#ffffff' : '#000000'} !important;
        }

        .react-tel-input .country-list .country .dial-code {
        color: ${isDarkTheme ? '#B0B0B0' : '#6B7280'} !important;
        }

        .react-tel-input .country-list .country:hover,
        .react-tel-input .country-list .country.highlight {
        background-color: ${isDarkTheme ? '#454545' : '#F8F8F8'} !important;
        }

        .react-tel-input .country-list .country:hover .dial-code,
        .react-tel-input .country-list .country.highlight .dial-code {
        color: ${isDarkTheme ? '#ffffff' : '#000000'} !important;
        }
      `}
			</style>
		</div>
	)
}

export default PhoneNumberField
